<template>
<div class="home-container" :style="backgroundStyle">
    <div class="bg-grid"></div>
    <div class="bg-orb orb-left"></div>
    <div class="bg-orb orb-right"></div>
    <!-- 动态粒子背景 -->
    <div class="particles">
        <div v-for="n in 50" :key="n" class="particle" :style="getParticleStyle()"></div>
    </div>
    
    <!-- 渐变遮罩层 -->
    <div class="gradient-overlay"></div>
    
    <!-- 主内容区域 -->
    <div class="content-wrapper">
        <div class="glass-card" :class="{ 'show': isLoaded }">
            <div class="eyebrow" :class="{ 'fade-in': showSubtitle }">
                <span class="pulse-dot"></span>
                <span class="eyebrow-text">稳定 · 快速 · 安全</span>
            </div>
            <!-- Logo/图标区域 -->
            <div class="logo-container">
                <div class="logo-ring">
                    <div class="logo-inner">
                        <LucideIcon name="rocket" :size="24" color="#fff" />
                    </div>
                </div>
            </div>
            
            <!-- 打字机效果标题 -->
            <h1 class="title">
                <span class="title-text">{{ displayedTitle }}</span>
                <span class="cursor" v-if="showCursor">|</span>
            </h1>
            
            <!-- 副标题渐入效果 -->
            <p class="subtitle" :class="{ 'fade-in': showSubtitle }">
                {{ msg }}
            </p>

            <div class="chip-row" :class="{ 'fade-in': showDivider }">
                <span class="chip">一键复制</span>
                <span class="chip">多端可用</span>
                <span class="chip">随时更新</span>
            </div>
            
            <!-- 分隔线动画 -->
            <div class="divider" :class="{ 'expand': showDivider }">
                <div class="divider-line"></div>
                <div class="divider-dot"></div>
                <div class="divider-line"></div>
            </div>
            
            <!-- 现代化按钮组 -->
            <div class="buttons-container" :class="{ 'show': showButtons }">
                <button 
                    v-for="(item, index) in menuItems" 
                    :key="item.title" 
                    class="nav-button"
                    :style="{ '--delay': index * 0.1 + 's' }"
                    @click="handleNavigation(item)"
                >
                    <span class="button-content">
                        <LucideIcon :name="item.icon" :size="20" />
                        <span class="button-text">{{ item.title }}</span>
                    </span>
                    <span class="button-glow"></span>
                    <span class="button-shine"></span>
                </button>
            </div>

            <p class="hint" :class="{ 'fade-in': showButtons }">支持 Clash / Shadowrocket / Stash</p>
            
            <!-- 底部装饰 -->
            <div class="footer-decoration">
                <div class="wave-line"></div>
            </div>
        </div>
    </div>
    
    <!-- 角落装饰元素 -->
    <div class="corner-decoration top-left"></div>
    <div class="corner-decoration top-right"></div>
    <div class="corner-decoration bottom-left"></div>
    <div class="corner-decoration bottom-right"></div>

    <!-- 登录弹窗 -->
    <transition name="modal">
        <div v-if="showLoginModal" class="modal-overlay" @click.self="showLoginModal = false">
            <div class="login-modal">
                <div class="modal-header">
                    <LucideIcon name="lock" :size="36" color="var(--color-primary)" />
                    <h2>管理员登录</h2>
                    <p>请输入管理员账号密码</p>
                </div>
                <div class="modal-body">
                    <input 
                        v-model="loginForm.username" 
                        type="text" 
                        placeholder="账号"
                        class="modal-input"
                        @keyup.enter="doLogin"
                    />
                    <input 
                        v-model="loginForm.password" 
                        type="password" 
                        placeholder="密码"
                        class="modal-input"
                        @keyup.enter="doLogin"
                    />
                    <p v-if="loginError" class="login-error">{{ loginError }}</p>
                </div>
                <div class="modal-footer">
                    <button class="modal-cancel" @click="showLoginModal = false">取消</button>
                    <button class="modal-confirm" @click="doLogin" :disabled="loginLoading">
                        {{ loginLoading ? '验证中...' : '登录' }}
                    </button>
                </div>
            </div>
        </div>
    </transition>
</div>
</template>
  
<script>
import backgroundMixin from '@/mixins/background'

export default {
name: 'HomePage',
mixins: [backgroundMixin],
data() {
    return {
        msg: '选择入口，快速获取订阅链接与客户端',
        menuItems: [
            { title: "用户管理", path: "/admin", isExternal: false, icon: "users", needAuth: true },
            { title: "节点健康", path: "/health", isExternal: false, icon: "signal", needAuth: false },
            { title: "软件下载", path: "/download", isExternal: false, icon: "package", needAuth: false },
        ],
        // 动画控制
        displayedTitle: "",
        fullTitle: "订阅中心",
        showCursor: true,
        showSubtitle: false,
        showDivider: false,
        showButtons: false,
        typingIndex: 0,
        // 登录弹窗
        showLoginModal: false,
        loginForm: { username: '', password: '' },
        loginError: '',
        loginLoading: false,
        pendingNavigation: null,
        // 定时器引用（用于组件销毁时清理）
        cursorTimer: null,
    }
},
methods: {
    handleNavigation(item) {
        if (item.isExternal) {
            window.open(item.path, '_blank', 'noopener,noreferrer');
            return;
        }
        // 需要认证的页面
        if (item.needAuth && !this.isLoggedIn()) {
            this.pendingNavigation = item.path;
            this.showLoginModal = true;
            this.loginError = '';
            this.loginForm = { username: '', password: '' };
            return;
        }
        this.$router.push(item.path);
    },
    // 检查是否已登录
    isLoggedIn() {
        return sessionStorage.getItem('admin_logged_in') === 'true';
    },
    // 执行登录
    async doLogin() {
        if (!this.loginForm.username || !this.loginForm.password) {
            this.loginError = '请输入账号和密码';
            return;
        }
        this.loginLoading = true;
        this.loginError = '';
        try {
            const res = await this.$axios.post('/api/auth/login', this.loginForm);
            if (res.data.success) {
                sessionStorage.setItem('admin_logged_in', 'true');
                this.showLoginModal = false;
                if (this.pendingNavigation) {
                    this.$router.push(this.pendingNavigation);
                    this.pendingNavigation = null;
                }
            }
        } catch (err) {
            this.loginError = err.response?.data?.message || '登录失败';
        } finally {
            this.loginLoading = false;
        }
    },
    startTypingAnimation() {
        const typeNextChar = () => {
            if (this.typingIndex < this.fullTitle.length) {
                this.displayedTitle += this.fullTitle[this.typingIndex];
                this.typingIndex++;
                setTimeout(typeNextChar, 150);
            } else {
                // 打字完成后的动画序列
                setTimeout(() => {
                    this.showCursor = false;
                    this.showSubtitle = true;
                }, 500);
                setTimeout(() => {
                    this.showDivider = true;
                }, 800);
                setTimeout(() => {
                    this.showButtons = true;
                }, 1100);
            }
        };
        setTimeout(typeNextChar, 800);
    },
    startCursorBlink() {
        this.cursorTimer = setInterval(() => {
            if (this.typingIndex < this.fullTitle.length) {
                this.showCursor = !this.showCursor;
            }
        }, 530);
    }
},
mounted() {
    // Mixin 已处理 fetchBackground() 和 triggerLoadAnimation()
    this.startCursorBlink();
    this.startTypingAnimation();
},
// 清理定时器，防止内存泄漏
beforeDestroy() {
    if (this.cursorTimer) {
        clearInterval(this.cursorTimer);
        this.cursorTimer = null;
    }
}
}
</script>

<style scoped>
/* ===== 基础容器样式 ===== */
.home-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    width: 100vw;
    overflow: hidden;
    transition: background-image 1s var(--ease-out);
    font-family: var(--font-display);
    color: var(--color-ink);
}

/* ===== 背景网格与光晕 ===== */
.bg-grid {
    position: absolute;
    inset: -20%;
    background-image:
        linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 80px 80px;
    opacity: 0.25;
    z-index: 0;
    -webkit-mask-image: radial-gradient(ellipse 70% 50% at 50% 10%, #000 60%, transparent 100%);
    mask-image: radial-gradient(ellipse 70% 50% at 50% 10%, #000 60%, transparent 100%);
}

.bg-orb {
    position: absolute;
    width: 520px;
    height: 520px;
    border-radius: 50%;
    filter: blur(40px);
    opacity: 0.55;
    z-index: 0;
    animation: orb-float 16s ease-in-out infinite;
}

.orb-left {
    top: -160px;
    left: -140px;
    background: radial-gradient(circle, rgba(110, 231, 255, 0.35), transparent 65%);
}

.orb-right {
    bottom: -180px;
    right: -120px;
    background: radial-gradient(circle, rgba(255, 209, 102, 0.35), transparent 65%);
    animation-delay: -6s;
}

@keyframes orb-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(18px); }
}

/* ===== 粒子动画 ===== */
.particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 1;
}

.particle {
    position: absolute;
    bottom: -20px;
    background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
    border-radius: 50%;
    animation: float-up linear infinite;
}

@keyframes float-up {
    0% { transform: translateY(0) rotate(0deg); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(-100vh) rotate(720deg); opacity: 0; }
}

/* ===== 渐变遮罩层 ===== */
.gradient-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(
        135deg,
        rgba(10, 16, 28, 0.88) 0%,
        rgba(18, 26, 44, 0.78) 55%,
        rgba(10, 18, 32, 0.92) 100%
    );
    z-index: 2;
}

/* ===== 内容包装器 ===== */
.content-wrapper {
    position: relative;
    z-index: 10;
    padding: var(--space-xl);
    width: 100%;
    max-width: 560px;
}

/* ===== 玻璃态卡片 ===== */
.glass-card {
    position: relative;
    background: linear-gradient(135deg, rgba(10, 16, 28, 0.68) 0%, rgba(16, 26, 44, 0.55) 55%, rgba(8, 14, 26, 0.72) 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: var(--radius-2xl);
    border: 1px solid var(--color-border);
    padding: var(--space-5xl) var(--space-4xl);
    overflow: hidden;
    text-align: center;
    box-shadow: 
        0 20px 50px rgba(0, 0, 0, 0.35),
        0 0 120px rgba(110, 231, 255, 0.12),
        inset 0 1px 1px rgba(255, 255, 255, 0.1);
    transform: translateY(30px) scale(0.95);
    opacity: 0;
    transition: all 0.8s var(--ease-out);
}

.glass-card::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: var(--radius-2xl);
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.45), rgba(255, 209, 102, 0.35), rgba(110, 231, 255, 0.2));
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
}

.glass-card.show {
    transform: translateY(0) scale(1);
    opacity: 1;
}

.eyebrow {
    display: inline-flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 6px 14px;
    margin: 0 auto 18px;
    border-radius: var(--radius-pill);
    border: 1px solid rgba(110, 231, 255, 0.25);
    background: rgba(11, 18, 32, 0.6);
    color: var(--color-muted);
    font-size: 0.82rem;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    opacity: 0;
    transform: translateY(8px);
    transition: all var(--duration-slow) ease-out;
}

.eyebrow.fade-in {
    opacity: 1;
    transform: translateY(0);
}

.pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-accent);
    box-shadow: var(--shadow-glow-accent);
    animation: pulse 1.6s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.4); opacity: 0.6; }
}

/* ===== Logo 区域 ===== */
.logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: var(--space-2xl);
}

.logo-ring {
    position: relative;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-ring::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px solid transparent;
    border-top-color: var(--color-primary);
    border-right-color: var(--color-primary-strong);
    border-radius: 50%;
    animation: spin 3s linear infinite;
}

.logo-ring::after {
    content: '';
    position: absolute;
    width: 70px;
    height: 70px;
    border: 2px solid transparent;
    border-bottom-color: var(--color-accent);
    border-left-color: rgba(255, 209, 102, 0.7);
    border-radius: 50%;
    animation: spin 2s linear infinite reverse;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.logo-inner {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-strong) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    box-shadow: 0 4px 20px rgba(110, 231, 255, 0.4);
}

/* ===== 标题样式 ===== */
.title {
    font-size: 3.1rem;
    font-weight: 800;
    margin-bottom: var(--space-lg);
    font-family: var(--font-display);
    background: linear-gradient(
        90deg, 
        var(--color-primary),
        #9ef3ff,
        var(--color-accent),
        var(--color-primary-strong),
        var(--color-primary)
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-flow 4s ease infinite;
    letter-spacing: 1.5px;
    filter: drop-shadow(0 0 20px rgba(110, 231, 255, 0.35));
}

@keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.cursor {
    color: var(--color-primary);
    animation: blink 0.8s infinite;
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

/* ===== 副标题样式 ===== */
.subtitle {
    font-size: 1.1rem;
    color: var(--color-muted);
    margin-bottom: var(--space-2xl);
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    opacity: 0;
    transform: translateY(10px);
    transition: all var(--duration-slow) ease-out;
}

.subtitle.fade-in {
    opacity: 1;
    transform: translateY(0);
}

.chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
    margin-bottom: var(--space-md);
    opacity: 0;
    transform: translateY(8px);
    transition: all var(--duration-slow) ease-out;
}

.chip-row.fade-in {
    opacity: 1;
    transform: translateY(0);
}

.chip {
    padding: 6px 12px;
    border-radius: var(--radius-pill);
    border: 1px solid rgba(110, 231, 255, 0.2);
    background: rgba(12, 20, 34, 0.55);
    color: var(--color-muted);
    font-size: 0.82rem;
    letter-spacing: 0.3px;
}

/* ===== 分隔线样式 ===== */
.divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    margin: 28px 0;
}

.divider-line {
    height: 1px;
    width: 0;
    background: linear-gradient(90deg, transparent, rgba(110, 231, 255, 0.6), transparent);
    transition: width 0.8s ease-out;
}

.divider.expand .divider-line {
    width: 80px;
}

.divider-dot {
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.4s ease-out 0.3s;
}

.divider.expand .divider-dot {
    transform: scale(1);
}

/* ===== 按钮容器 ===== */
.buttons-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
    margin-top: var(--space-sm);
}

.buttons-container.show .nav-button {
    opacity: 1;
    transform: translateY(0);
}

/* ===== 现代化按钮样式 ===== */
.nav-button {
    position: relative;
    width: 100%;
    padding: var(--space-lg) 28px;
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.18) 0%, rgba(255, 209, 102, 0.1) 100%);
    border: 1px solid rgba(110, 231, 255, 0.3);
    border-radius: var(--radius-lg);
    color: #fff;
    font-size: 1.1rem;
    font-weight: 500;
    cursor: pointer;
    overflow: hidden;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.4s var(--ease-out);
    transition-delay: var(--delay);
}

.nav-button:hover {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.35) 0%, rgba(255, 209, 102, 0.22) 100%);
    border-color: rgba(110, 231, 255, 0.6);
    transform: translateY(-3px) scale(1.02);
    box-shadow: 
        0 10px 40px rgba(110, 231, 255, 0.3),
        0 0 20px rgba(255, 209, 102, 0.2);
}

.nav-button:active {
    transform: translateY(0) scale(0.98);
}

.button-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.nav-button:hover .button-content .lucide-icon {
    transform: scale(1.2) rotate(10deg);
    transition: transform var(--duration-normal) ease;
}

.button-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(circle, rgba(110, 231, 255, 0.45) 0%, transparent 70%);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.5s ease;
    z-index: 0;
}

.nav-button:hover .button-glow {
    width: 400px;
    height: 400px;
}

.button-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: skewX(-20deg);
    z-index: 1;
}

.nav-button:hover .button-shine {
    animation: shine 0.8s ease;
}

@keyframes shine {
    0% { left: -100%; }
    100% { left: 200%; }
}

/* ===== 提示和底部装饰 ===== */
.hint {
    margin-top: 18px;
    font-size: 0.9rem;
    color: var(--color-subtle);
    opacity: 0;
    transform: translateY(8px);
    transition: all var(--duration-slow) ease-out;
}

.hint.fade-in {
    opacity: 1;
    transform: translateY(0);
}

.footer-decoration {
    margin-top: var(--space-3xl);
    display: flex;
    justify-content: center;
}

.wave-line {
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-accent), var(--color-primary-strong));
    border-radius: 2px;
    animation: wave 2s ease-in-out infinite;
}

@keyframes wave {
    0%, 100% { transform: scaleX(1); }
    50% { transform: scaleX(1.5); }
}

/* ===== 角落装饰 ===== */
.corner-decoration {
    position: absolute;
    width: 100px;
    height: 100px;
    z-index: 5;
    opacity: 0.2;
}

.top-left {
    top: 20px; left: 20px;
    border-top: 2px solid #4facfe;
    border-left: 2px solid #4facfe;
    border-radius: 10px 0 0 0;
}

.top-right {
    top: 20px; right: 20px;
    border-top: 2px solid #00f2fe;
    border-right: 2px solid #00f2fe;
    border-radius: 0 10px 0 0;
}

.bottom-left {
    bottom: 20px; left: 20px;
    border-bottom: 2px solid #f093fb;
    border-left: 2px solid #f093fb;
    border-radius: 0 0 0 10px;
}

.bottom-right {
    bottom: 20px; right: 20px;
    border-bottom: 2px solid #f5576c;
    border-right: 2px solid #f5576c;
    border-radius: 0 0 10px 0;
}

/* ===== 登录弹窗 ===== */
.modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.login-modal {
    width: 90%;
    max-width: 380px;
    background: linear-gradient(135deg, rgba(16, 24, 40, 0.92), rgba(20, 30, 52, 0.88));
    border: 1px solid rgba(110, 231, 255, 0.25);
    border-radius: var(--radius-xl);
    padding: 36px 28px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(110, 231, 255, 0.1);
}

.modal-header {
    text-align: center;
    margin-bottom: var(--space-2xl);
}

.modal-header .lucide-icon {
    display: block;
    margin: 0 auto 12px;
}

.modal-header h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 6px;
}

.modal-header p {
    font-size: 0.9rem;
    color: var(--color-subtle);
    margin: 0;
}

.modal-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: var(--space-2xl);
}

.modal-input {
    padding: 13px 18px;
    background: var(--color-surface-glass);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: var(--radius-md);
    color: #fff;
    font-size: 0.95rem;
    outline: none;
    transition: all var(--duration-normal) ease;
}

.modal-input::placeholder {
    color: rgba(255, 255, 255, 0.35);
}

.modal-input:focus {
    border-color: rgba(110, 231, 255, 0.6);
    box-shadow: 0 0 12px rgba(110, 231, 255, 0.12);
}

.login-error {
    color: var(--color-danger-light);
    font-size: 0.88rem;
    margin: 0;
    text-align: center;
}

.modal-footer {
    display: flex;
    gap: var(--space-md);
}

.modal-cancel, .modal-confirm {
    flex: 1;
    padding: var(--space-md);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--duration-normal) ease;
    border: 1px solid;
}

.modal-cancel {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
}

.modal-cancel:hover {
    background: rgba(255, 255, 255, 0.12);
}

.modal-confirm {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.3), rgba(58, 183, 255, 0.3));
    border-color: rgba(110, 231, 255, 0.4);
    color: #fff;
}

.modal-confirm:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.5), rgba(58, 183, 255, 0.5));
    box-shadow: 0 4px 16px rgba(110, 231, 255, 0.25);
}

.modal-confirm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* 弹窗动画 */
.modal-enter-active, .modal-leave-active {
    transition: opacity var(--duration-normal) ease;
}
.modal-enter-active .login-modal, .modal-leave-active .login-modal {
    transition: transform var(--duration-normal) ease;
}
.modal-enter, .modal-leave-to {
    opacity: 0;
}
.modal-enter .login-modal {
    transform: scale(0.9) translateY(20px);
}
.modal-leave-to .login-modal {
    transform: scale(0.95) translateY(10px);
}

/* ===== 响应式设计 ===== */
@media (max-width: 480px) {
    .glass-card {
        padding: 36px var(--space-2xl);
        border-radius: var(--radius-xl);
    }
    
    .title {
        font-size: 2.2rem;
    }
    
    .subtitle {
        font-size: 0.95rem;
    }
    
    .nav-button {
        padding: 14px var(--space-xl);
        font-size: 1rem;
    }
    
    .corner-decoration {
        width: 60px;
        height: 60px;
    }

    .login-modal {
        padding: 28px var(--space-xl);
    }
}

@media (min-width: 481px) and (max-width: 768px) {
    .content-wrapper {
        max-width: 480px;
    }
}

@media (min-width: 1024px) {
    .content-wrapper {
        max-width: 600px;
    }

    .glass-card {
        padding: 56px 48px;
    }
}
</style>
